/**
 * WebAuthn Service
 *
 * Handles WebAuthn passkey authentication for the frontend.
 * Provides secure, passwordless authentication using platform authenticators.
 */

export interface WebAuthnRegistrationOptions {
  rp: {
    name: string;
    id: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  challenge: string;
  pubKeyCredParams: Array<{
    type: "public-key";
    alg: number;
  }>;
  timeout: number;
  excludeCredentials: Array<{
    id: string;
    type: "public-key";
    transports?: AuthenticatorTransport[];
  }>;
  authenticatorSelection: {
    requireResidentKey: boolean;
    userVerification: UserVerificationRequirement;
  };
  attestation: AttestationConveyancePreference;
  sessionId: string;
}

export interface WebAuthnAuthenticationOptions {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials: Array<{
    id: string;
    type: "public-key";
    transports?: AuthenticatorTransport[];
  }>;
  userVerification: UserVerificationRequirement;
  sessionId: string;
}

export interface WebAuthnCredential {
  id: string;
  type: string;
  rawId: ArrayBuffer;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject?: ArrayBuffer;
    authenticatorData?: ArrayBuffer;
    signature?: ArrayBuffer;
    userHandle?: ArrayBuffer;
  };
}

export class WebAuthnService {
  private baseUrl: string;
  private lastCredential: any;

  constructor(baseUrl: string = '/api/v1/auth') {
    this.baseUrl = baseUrl;
  }

  /**
   * Check if WebAuthn is supported in the current browser
   */
  isSupported(): boolean {
    return window.PublicKeyCredential !== undefined;
  }

  /**
   * Check if the current platform supports passkeys
   */
  async isPasskeySupported(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.warn('Error checking passkey support:', error);
      return false;
    }
  }

  /**
   * Start passkey registration process
   */
  async startRegistration(username: string, displayName: string): Promise<WebAuthnRegistrationOptions> {
    try {
      const response = await fetch(`${this.baseUrl}/passkey/register/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          display_name: displayName,
        }),
      });

      if (!response.ok) {
        throw new Error(`Registration start failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.options;
    } catch (error) {
      console.error('Error starting passkey registration:', error);
      throw new Error('Failed to start passkey registration');
    }
  }

  /**
   * Complete passkey registration using browser WebAuthn API
   */
  async completeRegistration(options: WebAuthnRegistrationOptions): Promise<boolean> {
    try {
      // Convert base64url challenge to ArrayBuffer
      const challenge = this.base64urlToArrayBuffer(options.challenge);
      const userId = this.base64urlToArrayBuffer(options.user.id);

      // Create credential using WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: options.rp,
          user: {
            id: userId,
            name: options.user.name,
            displayName: options.user.displayName,
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          excludeCredentials: options.excludeCredentials.map(cred => ({
            id: this.base64urlToArrayBuffer(cred.id),
            type: cred.type as PublicKeyCredentialType,
            transports: cred.transports as AuthenticatorTransport[],
          })),
          authenticatorSelection: options.authenticatorSelection,
          attestation: options.attestation as AttestationConveyancePreference,
        },
      }) as WebAuthnCredential;

      if (!credential) {
        throw new Error('Credential creation was cancelled or failed');
      }

      // Send credential to server for verification
      const response = await fetch(`${this.baseUrl}/passkey/register/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: options.sessionId,
          credential: {
            id: credential.id,
            type: credential.type,
            rawId: this.arrayBufferToBase64url(credential.rawId),
            response: {
              clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
              attestationObject: credential.response.attestationObject
                ? this.arrayBufferToBase64url(credential.response.attestationObject)
                : undefined,
            },
          },
          device_name: this.getDeviceName(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Registration completion failed: ${response.statusText}`);
      }

      const result = await response.json();

      // Store the credential for MFA verification
      this.lastCredential = {
        id: credential.id,
        type: credential.type,
        rawId: this.arrayBufferToBase64url(credential.rawId),
        response: {
          clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
          attestationObject: credential.response.attestationObject
            ? this.arrayBufferToBase64url(credential.response.attestationObject)
            : undefined,
        },
        session_id: options.sessionId,
      };

      return result.success;
    } catch (error) {
      console.error('Error completing passkey registration:', error);
      throw new Error('Failed to complete passkey registration');
    }
  }

  /**
   * Get the last credential created during registration
   */
  async getLastCredential(): Promise<any> {
    return this.lastCredential;
  }

  /**
   * Start passkey authentication process
   */
  async startAuthentication(username?: string): Promise<WebAuthnAuthenticationOptions> {
    try {
      const response = await fetch(`${this.baseUrl}/passkey/authenticate/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
        }),
      });

      if (!response.ok) {
        throw new Error(`Authentication start failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.options;
    } catch (error) {
      console.error('Error starting passkey authentication:', error);
      throw new Error('Failed to start passkey authentication');
    }
  }

  /**
   * Complete passkey authentication using browser WebAuthn API
   */
  async completeAuthentication(options: WebAuthnAuthenticationOptions): Promise<{ success: boolean; user_id?: string }> {
    try {
      // Convert base64url challenge to ArrayBuffer
      const challenge = this.base64urlToArrayBuffer(options.challenge);

      // Get credential using WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          rpId: options.rpId,
          allowCredentials: options.allowCredentials.map(cred => ({
            id: this.base64urlToArrayBuffer(cred.id),
            type: cred.type as PublicKeyCredentialType,
            transports: cred.transports as AuthenticatorTransport[],
          })),
          userVerification: options.userVerification as UserVerificationRequirement,
          timeout: options.timeout,
        },
      }) as WebAuthnCredential;

      if (!credential) {
        throw new Error('Credential retrieval was cancelled or failed');
      }

      // Send credential to server for verification
      const response = await fetch(`${this.baseUrl}/passkey/authenticate/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: options.sessionId,
          credential: {
            id: credential.id,
            type: credential.type,
            rawId: this.arrayBufferToBase64url(credential.rawId),
            response: {
              clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
              authenticatorData: credential.response.authenticatorData
                ? this.arrayBufferToBase64url(credential.response.authenticatorData)
                : undefined,
              signature: credential.response.signature
                ? this.arrayBufferToBase64url(credential.response.signature)
                : undefined,
              userHandle: credential.response.userHandle
                ? this.arrayBufferToBase64url(credential.response.userHandle)
                : undefined,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Authentication completion failed: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error completing passkey authentication:', error);
      throw new Error('Failed to complete passkey authentication');
    }
  }

  /**
   * Get user's registered passkeys
   */
  async getPasskeys(userId: string): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/passkey/list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get passkeys: ${response.statusText}`);
      }

      const data = await response.json();
      return data.passkeys || [];
    } catch (error) {
      console.error('Error getting passkeys:', error);
      throw new Error('Failed to get passkeys');
    }
  }

  /**
   * Delete a passkey
   */
  async deletePasskey(credentialId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/passkey/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          credential_id: credentialId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete passkey: ${response.statusText}`);
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error deleting passkey:', error);
      throw new Error('Failed to delete passkey');
    }
  }

  /**
   * Utility: Convert base64url string to ArrayBuffer
   */
  public base64urlToArrayBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const base64Padded = base64 + padding;
    const binaryString = atob(base64Padded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Utility: Convert ArrayBuffer to base64url string
   */
  public arrayBufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const binaryString = String.fromCharCode(...bytes);
    const base64 = btoa(binaryString);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Get device name for passkey registration
   */
  private getDeviceName(): string {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    if (userAgent.includes('Mac')) {
      return 'Mac';
    } else if (userAgent.includes('Windows')) {
      return 'Windows PC';
    } else if (userAgent.includes('Linux')) {
      return 'Linux PC';
    } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      return 'iOS Device';
    } else if (userAgent.includes('Android')) {
      return 'Android Device';
    } else {
      return platform || 'Unknown Device';
    }
  }

  /**
   * Get authentication token from storage
   */
  private getAuthToken(): string {
    return localStorage.getItem('session_access_token') || '';
  }
}

// Export singleton instance
const webauthnService = new WebAuthnService();
export default webauthnService;
